import {
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  SpinnerWithFailed,
  TableColumnType,
  Text,
  Tooltip,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { getPageName } from '@fullstory/library-ui';
import {
  namedElementsSelectors,
  pagesSelectors,
  searchMetaSelectors,
  SpecObjectInfo,
} from '@fullstory/redux';
import { FieldScope, prettifyVarType } from '@fullstory/search-vars';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { PropertyRoleSelect } from '../../components/PropertyRoleSelect';
import { SampleValues } from '../../components/SampleValues';
import { propertyTypeLabels } from '../constants';
import { PropertyRow } from '../types';
import { forEachLeafProperty } from '../util';
import { useUpdatePropertyMapping } from './useUpdatePropertyMapping';

const { pagesByIdWithPageDefZero } = pagesSelectors;
const { getNamedElementsById } = namedElementsSelectors;

const MAX_CONNECTIONS_VALUES = {
  limit: 10,
  showMoreMessage: true,
};

type columnKey = 'displayName' | 'dataType' | 'propertyType' | 'connections' | 'role';
export const usePropertiesTableColumns = (
  retryFailedConnections: () => void,
  connectionParent: string | number | undefined,
  existingSpecObjects: SpecObjectInfo[],
  deemphasizeArchived: boolean = false,
): { [K in columnKey]: TableColumnType<PropertyRow> } => {
  const eventConnectionsByField = useSelector(searchMetaSelectors.getEventConnectionsByField);
  const eventConnectionsLoading = useSelector(searchMetaSelectors.getEventConnectionsLoading);
  const pageConnectionsByField = useSelector(searchMetaSelectors.getPageConnectionsByField);
  const pageConnectionsLoading = useSelector(searchMetaSelectors.getPageConnectionsLoading);
  const elementConnectionsByField = useSelector(searchMetaSelectors.getElementConnectionsByField);
  const elementConnectionsLoading = useSelector(searchMetaSelectors.getElementConnectionsLoading);
  const elements = useSelector(getNamedElementsById);
  const pages = useSelector(pagesByIdWithPageDefZero);

  const connections = useCallback(
    (property: PropertyRow) => {
      const labels = new Set<string>();
      let loading = false;
      let failed = false;
      forEachLeafProperty(property, prop => {
        switch (property.propertyType) {
          case FieldScope.Event: {
            const eventConnections = eventConnectionsByField.get(prop);
            loading ||= eventConnectionsLoading.has(prop);
            failed ||= !eventConnections && !loading;
            eventConnections?.forEach(name => labels.add(name));
            break;
          }
          case FieldScope.Page: {
            const pageConnections = pageConnectionsByField.get(prop);
            loading ||= pageConnectionsLoading.has(prop);
            failed ||= !pageConnections && !loading;
            pageConnections?.forEach(page => labels.add(getPageName(pages.get(page))));
            break;
          }
          case FieldScope.Element: {
            const elementConnections = elementConnectionsByField.get(prop);
            loading ||= elementConnectionsLoading.has(prop);
            failed ||= !elementConnections && !loading;
            elementConnections?.forEach(element =>
              labels.add(elements.get(element)?.namedElement?.name ?? ''),
            );
            break;
          }
        }
        return !loading && !failed;
      });
      return { labels, loading, failed };
    },
    [
      eventConnectionsByField,
      eventConnectionsLoading,
      pageConnectionsByField,
      pageConnectionsLoading,
      elementConnectionsByField,
      elementConnectionsLoading,
      elements,
      pages,
    ],
  );

  const propertyPendingState = React.useCallback(
    (property: PropertyRow) => {
      if (property.pending) {
        return 'secondary';
      }
      if (
        deemphasizeArchived &&
        (property.archived ||
          (connectionParent && property.archivedConnections.has(connectionParent)))
      ) {
        return 'disabled';
      }
      return undefined;
    },
    [connectionParent, deemphasizeArchived],
  );

  const existingSpecObjectSlugs = useMemo(
    () => existingSpecObjects.map(obj => obj.slug),
    [existingSpecObjects],
  );
  const onApplyPropertyMapping = useUpdatePropertyMapping(existingSpecObjects);

  return React.useMemo(
    () => ({
      displayName: {
        key: 'displayName',
        label: intl.formatMessage({ defaultMessage: 'Property Name', id: 'G9Im6D' }),
        sortable: true,
        // eslint-disable-next-line react/display-name
        renderCell: property => (
          <Flex justifyContent="flex-start" gap={12}>
            <Tooltip tooltipContent={property.displayName} childrenOverflowHidden>
              <Text appearance="bodySmall" color={propertyPendingState(property)} noWrap>
                {property.displayName}
              </Text>
            </Tooltip>
            {property.pending && (
              <Tooltip
                tooltipContent={intl.formatMessage({
                  defaultMessage: 'Waiting to receive data',
                  id: '8gVH3D',
                })}
              >
                <Icon name="information" color="secondary" />
              </Tooltip>
            )}
          </Flex>
        ),
      },
      dataType: {
        key: 'dataType',
        label: intl.formatMessage({ defaultMessage: 'Data Type', id: 'Ns1otQ' }),
        sortable: true,
        // eslint-disable-next-line react/display-name
        renderCell: property => (
          <Text appearance="bodySmall" color={propertyPendingState(property)}>
            {prettifyVarType(property.dataType, true)}
          </Text>
        ),
      },
      propertyType: {
        key: 'propertyType',
        label: intl.formatMessage({ defaultMessage: 'Property Type', id: 'FgYFTM' }),
        sortable: true,
        renderCell: property => (
          <Text appearance="bodySmall" color={propertyPendingState(property)}>
            {intl.formatMessage(
              {
                defaultMessage:
                  '{type, select, User {User} Page {Page} Event {Event} Element {Element} other {}}',
                id: 'wP7X3b',
              },
              { type: propertyTypeLabels[property.propertyType] },
            )}
          </Text>
        ),
      },
      connections: {
        key: 'connections',
        label: intl.formatMessage({ defaultMessage: 'Connections', id: 'l9CREU' }),
        // eslint-disable-next-line react/display-name
        renderCell: property => {
          if (property.pending) {
            return (
              <Text appearance="bodySmall" color={propertyPendingState(property)}>
                --
              </Text>
            );
          }
          const scopesWithConnections = [FieldScope.Event, FieldScope.Page, FieldScope.Element];
          if (!scopesWithConnections.includes(property.propertyType)) return null;
          const { labels, loading, failed } = connections(property);
          if (loading || failed) {
            if (retryFailedConnections) {
              return <SpinnerWithFailed failed={failed} onFailedRetry={retryFailedConnections} />;
            } else {
              return <Spinner />;
            }
          }
          const sampleValues = [...labels.values()].sort();
          const showMoreContent = (
            <Button variant="underline-builder" size="x-small">
              <Text appearance="bodySmall">{sampleValues.length}</Text>
            </Button>
          );
          return (
            <SampleValues
              sampleValues={sampleValues}
              maxValues={MAX_CONNECTIONS_VALUES}
              emptyMessage={intl.formatMessage({ defaultMessage: 'No connections', id: 'medMEK' })}
              showMoreContent={showMoreContent}
              justifyContent="flex-start"
            />
          );
        },
      },
      role: {
        key: 'role',
        label: (
          <>
            <Text appearance="bodySmallBold">
              <FormattedMessage defaultMessage="Role" id="1ZgrhW" />
            </Text>
          </>
        ),
        renderCell: property => (
          <Box collapsible>
            <PropertyRoleSelect
              property={property}
              parentSlugFilter={existingSpecObjectSlugs}
              onApply={onApplyPropertyMapping}
              disabled={!!propertyPendingState(property)} // can't select anything if we are pending
            />
          </Box>
        ),
      },
    }),
    [connections, retryFailedConnections, propertyPendingState, existingSpecObjectSlugs],
  );
};
